// @ts-check
// =============================================================================
// js/ui/nav-shell.js — the persistent header: nav buttons, connection dot,
// the profile chip (opens the player profile popup), and the rewards badge
// (plan/main-menu.md).
//
// WHAT THIS IS
//   The frame around every routed screen (old client: Header() :3774). It is
//   TIER-AWARE (guests see Join/Login instead of the account items) and
//   CONTEXT-AWARE (a "Game" item appears while a duel is live).
//
//   Mounted once by boot.js after login; re-renders itself on session /
//   connection / userData / duel changes.
// =============================================================================

import { store, subscribe } from '../core/store.js';
import { navigate, currentPath } from '../core/router.js';
import { can, showLoginOptions, JOIN_URL } from '../app/session.js';
import { isShell } from '../app/shell-mode.js';
import { openProfile } from './profile.js';
import { openModal } from './modal.js';
import { requestGameFullscreen } from './fullscreen.js';

// The Links popup destinations (nav "Links" item; every tier sees it).
// smutty.games keeps the plain-http scheme on purpose: the site redirects as
// it sees fit.
const LINKS = [
	{ label: 'Smutty.Games', url: 'http://smutty.games' },
	{ label: 'Patreon', url: 'https://www.patreon.com/smuttygames' },
	{ label: 'Discord', url: 'https://discord.gg/EaK4C8g3Cf' },
];

/** The Links popup: each button opens its destination in a new tab. */
function openLinksPopup() {
	openModal({
		title: 'Links',
		buttons: [
			...LINKS.map(({ label, url }) => ({
				label,
				onClick: () => window.open(url, '_blank', 'noopener'),
			})),
			{ label: 'Close' },
		],
	});
}

/** Debounce for the resize re-measure (the fit test needs settled layout). */
let resizeTimer = /** @type {ReturnType<typeof setTimeout>|null} */ (null);

/** Connection emoji by store.connection value (old UpdateSocketStatus). */
const CONNECTION_DOT = {
	idle: '⚫',
	connected: '🟢',
	reconnecting: '🟡',
	down: '🔴',
};

/** Hover tooltip for the status orb, matching CONNECTION_DOT's states. */
const CONNECTION_LABEL = {
	idle: 'Not connected',
	connected: 'Connected to server',
	reconnecting: 'Reconnecting to server…',
	down: 'Unable to connect — retrying',
};

/**
 * The status orb's tooltip text for a connection state.
 * @param {string} connection
 * @returns {string}
 */
function connectionTitle(connection) {
	return CONNECTION_LABEL[connection] || 'Connection status unknown';
}

/** @type {HTMLElement|null} */
let mountEl = null;

/** @type {Array<() => void>} store unsubscribers */
let stops = [];

/**
 * Mount the nav shell. Call once from boot.js.
 * @param {HTMLElement} el
 * @returns {void}
 */
export function mountNavShell(el) {
	mountEl = el;
	stops.forEach((stop) => stop());
	stops = [
		subscribe('session', renderNav),
		subscribe('connection', renderNav),
		subscribe('userData', renderNav),
		subscribe('duel', renderNav),
	];
	window.addEventListener('hashchange', renderNav);
	// The collapse is MEASURED (does the full row fit?), so any resize can
	// flip it either way — re-render on a short debounce.
	window.addEventListener('resize', () => {
		if (resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(renderNav, 120);
	});
	renderNav();
}

/**
 * One nav button.
 * @param {string} label
 * @param {string} [path]      Router path it navigates to (omitted for
 *                             popup-style items like Links).
 * @param {string} [badge]     Optional badge text (rewards count).
 * @param {() => void} [onClick]  Popup items: run this instead of navigating.
 * @returns {HTMLButtonElement}
 */
function navButton(label, path, badge, onClick) {
	const btn = document.createElement('button');
	btn.className = 'headerButton' + (path && currentPath().startsWith(path.split('/')[0]) ? ' is-active' : '');
	btn.textContent = label;
	if (badge) {
		const badgeEl = document.createElement('span');
		badgeEl.className = 'nav-badge';
		badgeEl.textContent = badge;
		btn.appendChild(badgeEl);
	}
	btn.addEventListener('click', () => {
		if (onClick) {
			onClick();
			return;
		}
		// The "Game" item returns the player to their live duel — grab mobile
		// fullscreen off this click too (ui/fullscreen.js), like the lobby's
		// game-entry buttons, so the duel doesn't need one more tap.
		if (path === 'duel') requestGameFullscreen();
		navigate(path);
	});
	return btn;
}

/**
 * The nav destinations for the current session (shared by the full row and
 * the compact popup). Most items route via `path`; popup-style items (Links)
 * carry an `onClick` instead.
 * @returns {{items: Array<{label:string, path?:string, badge?:string, onClick?:() => void}>, unseen: number}}
 */
function navItems() {
	const { session, userData, duel } = store;
	/** @type {Array<{label:string, path:string, badge?:string}>} */
	const items = [{ label: 'Lobby', path: 'lobby' }];
	if (duel) {
		items.push({ label: 'Game', path: 'duel' });
	}
	items.push({ label: session.tier === 'paid' ? 'Cardpool' : 'Cards', path: 'gallery' });
	// Rewards badge counts packs earned since last viewed (old :3735).
	const packs = userData?.collection?.packs?.length || 0;
	const seen = Number(localStorage.getItem('rewardsSeen') || 0);
	const unseen = Math.max(0, packs - seen);
	if (can('earnPacks')) {
		items.push({ label: 'Rewards', path: 'rewards', badge: unseen ? String(unseen) : undefined });
	}
	items.push({ label: 'Guide', path: 'help' });
	// Community links (popup, not a route) — every tier sees it.
	items.push({ label: 'Links', onClick: openLinksPopup });
	if (can('settings')) {
		items.push({ label: 'Settings', path: 'settings' });
	}
	return { items, unseen };
}

/** The compact nav's popup: every destination as a tappable list row. */
function openNavMenu() {
	const { session, userData } = store;
	const { items } = navItems();
	const buttons = [
		// Profile first — it replaces the 👤 chip in compact mode.
		{ label: `👤 ${userData?.nickname || 'Profile'}`, onClick: openProfile },
		...items.map((item) => ({
			label: item.badge ? `${item.label} (${item.badge})` : item.label,
			onClick: item.onClick || (() => {
				// Same as navButton: re-entering the live duel grabs fullscreen
				// off the tap that does it.
				if (item.path === 'duel') requestGameFullscreen();
				navigate(item.path);
			}),
		})),
	];
	if (session.isGuest) {
		// Shell (itch etc.): no Login, and Join opens a REAL tab — see shell-mode.js.
		if (isShell()) {
			buttons.push({ label: 'Join', onClick: () => window.open(JOIN_URL, '_blank', 'noopener') });
		} else {
			buttons.push({ label: 'Join', onClick: () => { window.location.href = JOIN_URL; } });
			buttons.push({ label: 'Login', onClick: showLoginOptions });
		}
	}
	buttons.push({ label: 'Close' });
	openModal({ title: 'Menu', buttons });
	// Buttons stack full-width via .modal-actions (see ui/modal.js + CSS).
}

/**
 * Does the mounted full row actually FIT? Not a breakpoint — a measurement:
 * the row fails when its buttons wrapped onto a second line, or when the
 * absolutely-positioned status corner overlaps the leftmost button.
 * @param {HTMLElement} nav  The mounted .nav-shell.
 * @returns {boolean}
 */
function rowOverflows(nav) {
	const buttons = [...nav.querySelectorAll('.headerButton')];
	if (!buttons.length) return false;
	const firstTop = buttons[0].offsetTop;
	if (buttons.some((btn) => btn.offsetTop > firstTop + 4)) return true; // wrapped
	const status = nav.querySelector('.nav-status');
	if (status && getComputedStyle(status).position === 'absolute') {
		const statusRight = status.getBoundingClientRect().right;
		const firstLeft = Math.min(...buttons.map((btn) => btn.getBoundingClientRect().left));
		if (statusRight > firstLeft - 6) return true; // collides with the corner
	}
	return false;
}

/** The collapsed header: ONE "● Menu" button, top-LEFT (matching the duel's
 *  own menu placement). The rewards count lives only on the Rewards row
 *  inside the popup — no badge out here. */
function renderCompactNav(nav) {
	nav.innerHTML = '';
	nav.classList.add('nav-compact');
	const btn = document.createElement('button');
	btn.className = 'headerButton nav-menu-btn';
	const dot = document.createElement('span');
	dot.className = 'nav-menu-dot';
	dot.textContent = CONNECTION_DOT[store.connection] || '⚫';
	dot.title = connectionTitle(store.connection);
	btn.append(dot, document.createTextNode(' Menu'));
	btn.addEventListener('click', openNavMenu);
	nav.appendChild(btn);
}

/** Rebuild the header from current store state. */
function renderNav() {
	if (!mountEl) return;
	const { session, userData, duel, connection } = store;
	mountEl.innerHTML = '';

	// In-duel the shell disappears at every size — the duel is a fullscreen
	// PIXI takeover with its own ☰ Menu in the corner. No header bar over the
	// board, desktop included: the duel's own menu is the only chrome.
	const path = currentPath();
	if (duel && path === 'duel') {
		return;
	}
	// The deck builder has its own toolbar (⇦ Lobby, name, save/delete, export)
	// — the global nav on top of it was a redundant second header. Hide it here
	// too; the toolbar's ⇦ Lobby is the way back.
	if (path.split('/')[0] === 'deck') {
		return;
	}

	const nav = document.createElement('nav');
	nav.className = 'nav-shell';

	// Status corner: connection dot + the profile chip. The chip opens the
	// player profile popup; its "Welcome <name>" text hides on narrow screens
	// (CSS .nav-welcome-text) where there isn't room — the 👤 stays tappable.
	const status = document.createElement('div');
	status.className = 'nav-status';
	status.innerHTML = `<span id="socketStatus" title="${connectionTitle(connection)}">${CONNECTION_DOT[connection] || '⚫'}</span>`;
	if (userData) {
		const chip = document.createElement('button');
		chip.className = 'nav-welcome';
		chip.title = 'Your profile';
		const icon = document.createElement('span');
		icon.textContent = '👤';
		const text = document.createElement('span');
		text.className = 'nav-welcome-text';
		text.textContent = 'Welcome ' + (userData.nickname || 'Biter');
		chip.append(icon, text);
		chip.addEventListener('click', openProfile);
		status.appendChild(chip);
	}
	nav.appendChild(status);

	// Primary nav (tier-aware, shared with the compact popup).
	for (const item of navItems().items) {
		nav.appendChild(navButton(item.label, item.path, item.badge, item.onClick));
	}

	if (session.isGuest) {
		// Guests: upsell entry points alongside the game items (old :3679).
		// Shell (itch etc.): Join opens a REAL tab and Login disappears entirely —
		// OAuth can't run inside a portal shell (shell-mode.js).
		const joinBtn = document.createElement('button');
		joinBtn.className = 'headerButton';
		joinBtn.textContent = 'Join';
		joinBtn.addEventListener('click', () => {
			if (isShell()) window.open(JOIN_URL, '_blank', 'noopener');
			else window.location.href = JOIN_URL;
		});
		nav.appendChild(joinBtn);

		if (!isShell()) {
			const loginBtn = document.createElement('button');
			loginBtn.className = 'headerButton';
			loginBtn.textContent = 'Login';
			loginBtn.addEventListener('click', showLoginOptions);
			nav.appendChild(loginBtn);
		}
	}

	mountEl.appendChild(nav);

	// MEASURED collapse: only when the full row genuinely doesn't have the
	// room does the header fold. First relief valve: drop the "Welcome
	// <name>" text (the 👤 chip stays tappable) — a long random nickname
	// must not cost the whole row. Still overflowing after that (wrapped
	// buttons or a colliding corner) -> the single "● Menu" button.
	if (rowOverflows(nav)) {
		nav.classList.add('nav-tight');
		if (rowOverflows(nav)) {
			renderCompactNav(nav);
		}
	}
}
